import {Fragment} from 'react';
import {Table} from '@goproblems/ui';
import {SgfDiff, VisualDiff} from '@types';
import {useTheme} from '@components/theme/useTheme';
import {useTranslation} from 'react-i18next';

function constructUrl(
    baseUrl: string,
    queryString: string,
    defaultSgf: string
) {
    if (!queryString.includes('sgf=')) {
        const sgfParam = 'sgf=' + encodeURIComponent(defaultSgf);
        if (queryString) {
            queryString += '&' + sgfParam;
        } else {
            queryString = sgfParam;
        }
    }
    return baseUrl + '?' + queryString;
}

const SgfDiffRow = ({
    diff,
    originalSgf,
}: {
    diff: SgfDiff;
    originalSgf: string;
}) => {
    const {t} = useTranslation();
    const visArray = diff.vis;

    const visItem = visArray[0];
    const step1Url = constructUrl(
        '/api/v2/tools/sgf/renderext',
        visItem.step1,
        originalSgf
    );
    const step2Url = constructUrl(
        '/api/v2/tools/sgf/renderext',
        visItem.step2,
        originalSgf
    );

    const step3Url = constructUrl(
        '/api/v2/tools/sgf/renderext',
        visItem.step3,
        originalSgf
    );

    return (
        <>
            <tr className="font-semibold diff-row border-t-[1px]">
                <td>{diff.type}</td>
                <td>
                    <img src={step1Url} alt={t('sgf_diff.before_position')} />
                </td>
                <td>
                    <img src={step2Url} alt={t('sgf_diff.after_position')} />
                </td>
                <td>{visItem.txt}</td>
                {visItem.step3 && (
                    <td>
                        <img src={step3Url} alt={t('sgf_diff.result_position')} />
                    </td>
                )}
            </tr>
        </>
    );
};

const SgfSequenceRow = ({
    sequence,
    index,
    originalSgf,
}: {
    sequence: VisualDiff;
    index: number;
    originalSgf: string;
}) => {
    const {t} = useTranslation();
    const step1Url = constructUrl(
        '/api/v2/tools/sgf/renderext',
        sequence.step1,
        originalSgf
    );
    const step2Url = constructUrl(
        '/api/v2/tools/sgf/renderext',
        sequence.step2,
        originalSgf
    );
    const step3Url = constructUrl(
        '/api/v2/tools/sgf/renderext',
        sequence.step3,
        originalSgf
    );

    return (
        <>
            <tr className="sequence-row text-sm">
                <td>{t('sgf_diff.sequence_number', {number: index + 1})}</td>
                <td>
                    <img width={100} src={step1Url} alt={t('sgf_diff.before_position')} />
                </td>
                <td>
                    <img width={100} src={step2Url} alt={t('sgf_diff.after_position')} />
                </td>
                <td>{sequence.txt}</td>
                {sequence.step3 && (
                    <td>
                        <img width={100} src={step3Url} alt={t('sgf_diff.result_position')} />
                    </td>
                )}
            </tr>
        </>
    );
};

export const SgfDiffContent = ({
    originalSgf,
    sgfDiffs,
}: {
    originalSgf?: string;
    sgfDiffs: SgfDiff[];
}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const headerBgClass = theme === 'dark' ? 'bg-neutral-900' : 'bg-neutral-100';
    return (
        <Table striped size="sm" responsive>
            <tr className={headerBgClass}>
                <th className="p-2">{t('common.type')}</th>
                <th className="p-2">{t('sgf_diff.sequence')}</th>
                <th className="p-2">{t('sgf_diff.position')}</th>
                <th className="p-2">{t('common.description')}</th>
                <th className="p-2"></th>
            </tr>
            {originalSgf &&
                sgfDiffs?.map((diff, index) => (
                    <Fragment key={index}>
                        <SgfDiffRow
                            key={index}
                            diff={diff}
                            originalSgf={originalSgf}
                        />
                        {diff.vis.map((sequence, index) => (
                            <SgfSequenceRow
                                key={index}
                                sequence={sequence}
                                index={index}
                                originalSgf={originalSgf}
                            />
                        ))}
                    </Fragment>
                ))}
        </Table>
    );
};
